import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, CircleDashed, Circle, Lock, Search } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Panel, StatusChip } from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import type { SeoCheckItem, SeoCheckStatus } from '@/lib/seoChecklist';

const STATUS_ICON: Record<SeoCheckStatus, typeof Circle> = {
  done: CheckCircle2,
  partial: CircleDashed,
  todo: Circle,
  locked: Lock,
};

const STATUS_LABEL: Record<SeoCheckStatus, string> = {
  done: 'Done',
  partial: 'Could be better',
  todo: 'To do',
  locked: 'Upgrade',
};

function scoreTone(score: number) {
  if (score >= 80) return { tone: 'green' as const, label: 'Looking good' };
  if (score >= 50) return { tone: 'amber' as const, label: 'Getting there' };
  return { tone: 'rose' as const, label: 'Needs attention' };
}

export interface SeoCheckCardProps {
  items: SeoCheckItem[];
  score: number;
  className?: string;
}

/** "Google & SEO" checklist: a score plus plain-English fixes, each linking to where it's fixed. */
export function SeoCheckCard({ items, score, className }: SeoCheckCardProps) {
  const { tone, label } = scoreTone(score);
  const ordered = [...items].sort((a, b) => order(a.status) - order(b.status) || b.weight - a.weight);

  return (
    <Panel
      className={className}
      title="SEO check"
      icon={<Search className="h-4 w-4 text-muted-foreground" />}
      description="How ready your website is to be found on Google. Fix the top items first."
      actions={
        <StatusChip tone={tone} data-testid="seo-score-label">
          {label}
        </StatusChip>
      }
    >
      <div className="mb-4 flex items-center gap-3">
        <span className="text-2xl font-semibold tabular-nums text-foreground" data-testid="seo-score">
          {score}
          <span className="text-sm font-normal text-muted-foreground">/100</span>
        </span>
        <Progress value={score} className="h-2 flex-1" aria-label={`SEO score ${score} out of 100`} />
      </div>

      <ul className="space-y-2" aria-label="SEO checklist">
        {ordered.map((item) => {
          const Icon = STATUS_ICON[item.status];
          return (
            <li key={item.id} data-testid={`seo-item-${item.id}`} data-status={item.status}>
              <Link
                to={item.href}
                className={cn(
                  'group flex items-start justify-between gap-3 rounded-lg border p-3 transition-colors',
                  item.status === 'done'
                    ? 'border-transparent bg-muted/40'
                    : 'border-border bg-background hover:border-primary/40 hover:bg-muted/40',
                )}
              >
                <div className="flex min-w-0 items-start gap-3">
                  <Icon
                    aria-hidden
                    className={cn(
                      'mt-0.5 h-5 w-5 shrink-0',
                      item.status === 'done' && 'text-primary',
                      item.status === 'partial' && 'text-amber-500 dark:text-amber-400',
                      (item.status === 'todo' || item.status === 'locked') && 'text-muted-foreground',
                    )}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {item.title}
                      <span className="sr-only"> — {STATUS_LABEL[item.status]}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{item.detail}</p>
                  </div>
                </div>
                <span className="flex shrink-0 items-center gap-1 whitespace-nowrap pt-0.5 text-xs font-medium text-primary">
                  <span className="hidden sm:inline">{item.action}</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function order(status: SeoCheckStatus) {
  return status === 'todo' ? 0 : status === 'partial' ? 1 : status === 'locked' ? 2 : 3;
}
